import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
//components
import Dyops from '../../atoms/dyops';
import ChromaticPresetPanel from '../../moleculs/chromaticPreset';
import { ChromaticPresetWarnings } from '../../moleculs/chromaticPreset/ChromaticPresetWarnings';
import ExamModeSegment from '../../moleculs/examModeSegment';
import OvalAdjustGlyph from '../../atoms/ovalAdjustGlyph';
import OvalRefractionPanel from '../../moleculs/ovalRefractionPanel';
import DoctorTestModalDyopPreview from '../../moleculs/doctorTestModalDyopPreview';
//actions
import dyopsAction from '../../../redux/actions/dyops';
import userAction from '../../../redux/actions/user';
// api
import { apiDoctor } from '../../../api/doctor';
// types
import { dyopsType } from '../../../types/dyops';
import { loadingType, roomDataType, stateType } from '../../../types/types';
// style
import './index.scss';
// helpers
import { useWindowSize } from '../../../tools';
import { attachScreenWakeLock } from '../../../tools/screenWakeLock';
import { handleDyopsControl } from '../../../tools/dyopsControl';
import { getChromaticFieldsFromDoctorUser } from '../../../tools/doctorChromaticPreference';
import { getOvalFieldsFromDoctorUser } from '../../../tools/doctorOvalPreference';
import {
  flushScheduledPersistDoctorOvalPreference,
  schedulePersistDoctorOvalPreference,
} from '../../../tools/persistDoctorOvalPreference';
import { applyExamModeToDyops } from '../../../tools/applyOvalDyops';
import { dyopPreviewSpinDurationSec, patientTestDyopDiameterPx } from '../../../tools/dyopPreviewSpin';
import type { ExamMode } from '../../../types/dyops';
// websockets
import { webSocketConnect } from '../../../websocket/websocket';
// localization
import strings from '../../../localization';
import { useInfantTargetJump } from '../../../hooks/useInfantTargetJump';
import { applyInfantModeToDyops, isInfantTestType } from '../../../tools/infantDefaults';
import { getPatientTestTypeLabel } from '../../../tools/infantResultDisplay';
import { resolveDoctorSessionTestType } from '../../../tools/patientOnboardingSelection';
import { apiApp } from '../../../api/app';

// Renders the sun glyph for the chromatic scheme trigger button.
const ChromaticSchemeGlyph: FC = () => (
  <svg
    className="doctor-test__chromatic-modal-icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={true}
  >
    <circle cx="12" cy="12" r="3.5" />
    <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" />
  </svg>
);

interface DoctorTestProps {
  appLoading: loadingType;
  isConnected: boolean;
  setIsConnected: (isConnected: boolean) => void;
  isUserConnected: boolean;
  setIsUserConnected: (isUserConnected: boolean) => void;
  roomId: string;
  testType?: string;
  isIntegration: boolean;
}

const DoctorTest: FC<DoctorTestProps> = ({
  appLoading,
  setIsConnected,
  isConnected,
  isUserConnected,
  setIsUserConnected,
  roomId,
  testType,
  isIntegration,
}: DoctorTestProps) => {
  const [randomNumbers, setRandomNumbers] = useState<Array<number>>([]);
  const [chromaticModalOpen, setChromaticModalOpen] = useState(false);
  const [ovalModalOpen, setOvalModalOpen] = useState(false);
  const [examToolsModalOpen, setExamToolsModalOpen] = useState(false);
  const [chromaticModalWarnings, setChromaticModalWarnings] = useState({
    contrastLow: false,
    customInvalid: false,
  });
  const [chromaticPreviewDyops, setChromaticPreviewDyops] = useState<dyopsType | null>(null);
  const [ovalPreviewDyops, setOvalPreviewDyops] = useState<dyopsType | null>(null);
  const [patientSessionTestType, setPatientSessionTestType] = useState<string | undefined>();

  const handleChromaticWarningsChange = useCallback((state: { contrastLow: boolean; customInvalid: boolean }) => {
    setChromaticModalWarnings(state);
  }, []);

  useEffect(() => {
    if (!chromaticModalOpen) {
      setChromaticModalWarnings({ contrastLow: false, customInvalid: false });
      setChromaticPreviewDyops(null);
    }
  }, [chromaticModalOpen]);

  useEffect(() => {
    if (!ovalModalOpen) {
      setOvalPreviewDyops(null);
    }
  }, [ovalModalOpen]);

  useEffect(() => {
    if (chromaticModalOpen || ovalModalOpen) {
      setExamToolsModalOpen(false);
    }
  }, [chromaticModalOpen, ovalModalOpen]);

  const examOverlayOpen = chromaticModalOpen || ovalModalOpen || examToolsModalOpen;

  const history = useHistory();
  const dispatch = useDispatch();
  const [windowWidth, windowHeight] = useWindowSize();
  const socket = useRef<any>();
  const height = windowHeight > windowWidth && isMobile;
  const landscapeMobile = isMobile && windowWidth > windowHeight;

  // Lock page scroll while an exam modal is open.
  // Android Chrome breaks nested overflow scroll when a non-passive document touchmove
  // only calls preventDefault selectively, so drive modal scrollTop from touch deltas.
  useEffect(() => {
    if (!examOverlayOpen) {
      return undefined;
    }

    const html = document.documentElement;
    const body = document.body;
    const contentInner = document.querySelector('.content_test .content__inner') as HTMLElement | null;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevContentOverflow = contentInner?.style.overflow ?? '';

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    if (contentInner) {
      contentInner.style.overflow = 'hidden';
    }

    const modalScrollSelector =
      '.doctor-test__chromatic-modal-controls, .doctor-test__chromatic-modal-body, .doctor-test__exam-tools-modal-body';
    // Ignore tiny finger jitter so a tap is not turned into a scroll that cancels click.
    const scrollSlopPx = 10;

    /** Finds the modal overflow container for a touch target. */
    const findModalScrollEl = (target: EventTarget | null) => {
      if (!(target instanceof Element)) {
        return null;
      }
      return target.closest(modalScrollSelector) as HTMLElement | null;
    };

    /** True when the touch is on a control that must keep native tap/click (incl. radio labels). */
    const isModalFormControl = (target: EventTarget | null) => {
      if (!(target instanceof Element)) {
        return false;
      }
      return Boolean(
        target.closest(
          'input, textarea, select, button, label, a, .radio, [role="button"], [role="radio"], [role="slider"]',
        ),
      );
    };

    let activeScrollEl: HTMLElement | null = null;
    let lastClientY = 0;
    let startClientY = 0;
    let scrollArmed = false;

    /** Starts tracking a modal scroll gesture. */
    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        activeScrollEl = null;
        scrollArmed = false;
        return;
      }

      startClientY = event.touches[0].clientY;
      lastClientY = startClientY;
      scrollArmed = false;

      if (isModalFormControl(event.target)) {
        activeScrollEl = null;
        return;
      }

      const el = findModalScrollEl(event.target);
      if (!el || el.scrollHeight <= el.clientHeight + 1) {
        activeScrollEl = null;
        return;
      }

      activeScrollEl = el;
    };

    /** Scrolls the modal body; blocks background page scroll while the overlay is open. */
    const onTouchMove = (event: TouchEvent) => {
      if (activeScrollEl && event.touches.length === 1) {
        const touch = event.touches[0];
        const deltaFromStart = Math.abs(touch.clientY - startClientY);

        // Until the finger clearly moves, do not preventDefault or the first tap is lost.
        if (!scrollArmed) {
          if (deltaFromStart < scrollSlopPx) {
            return;
          }
          scrollArmed = true;
        }

        activeScrollEl.scrollTop += lastClientY - touch.clientY;
        lastClientY = touch.clientY;
        event.preventDefault();
        return;
      }

      if (isModalFormControl(event.target) && findModalScrollEl(event.target)) {
        return;
      }

      // Outside scrollable modal chrome: block background page scroll only after a real drag.
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        if (Math.abs(touch.clientY - startClientY) < scrollSlopPx && !scrollArmed) {
          return;
        }
      }

      event.preventDefault();
    };

    /** Clears the active modal scroll gesture. */
    const onTouchEnd = () => {
      activeScrollEl = null;
      scrollArmed = false;
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchEnd);

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (contentInner) {
        contentInner.style.overflow = prevContentOverflow;
      }
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [examOverlayOpen]);

  const dyopStageDiameter = landscapeMobile ? 40 : isMobile ? 44 : windowWidth <= 1100 ? 53 : 66;
  const dyopEyeSeparationPx = isMobile ? (landscapeMobile ? 32 : 32) : undefined;

  // websockets
  const accessToken =
    isIntegration && sessionStorage.getItem('integrationToken')
      ? sessionStorage.getItem('integrationToken')
      : localStorage.getItem('accessToken')
      ? localStorage.getItem('accessToken')
      : '';
  const socketUrl = process.env.REACT_APP_API_SOCKET_URL || '';

  // store
  const dyops = useSelector((state: stateType) => state.dyops.dyops);
  const controlsVisible = !appLoading.status && isConnected && dyops.isStarted && isUserConnected;
  const appData = useSelector((state: stateType) => state.app.app);
  const user = useSelector((state: stateType) => state.user.user);

  const doctorName = `${user?.firstName} ${user?.lastName}`;

  const resolvedTestType = resolveDoctorSessionTestType(testType, patientSessionTestType);
  // Prefer known session type so a leftover infantMode flag cannot force empty infant disks on Adult/Oval.
  const isInfantSession = resolvedTestType
    ? isInfantTestType(resolvedTestType)
    : Boolean(dyops.infantMode) || isInfantTestType(dyops.result?.testType);
  const startDisabled =
    !isUserConnected || (!isInfantSession && (dyops.closeRightEye || dyops.closeLeftEye) && dyops.isStarted);
  /** Hide Start / Test Left|Right once that eye is spinning (button used to stay disabled on screen). */
  const showStartButton =
    !(isInfantSession && dyops.isStarted) && !(dyops.isStarted && (dyops.closeLeftEye || dyops.closeRightEye));
  const resolvedTestTypeRef = useRef(resolvedTestType);
  resolvedTestTypeRef.current = resolvedTestType;
  const dyopsRef = useRef(dyops);
  dyopsRef.current = dyops;

  const effectiveDyops = useMemo(
    () => (isInfantSession ? applyInfantModeToDyops(dyops, 'infant') : dyops),
    [dyops, isInfantSession],
  );

  const activeSessionTestType = resolvedTestType || dyops.result?.testType || (isInfantSession ? 'infant' : undefined);
  const sessionTestTypeLabel = getPatientTestTypeLabel(activeSessionTestType);

  const chromaticModalWasOpen = useRef(false);
  const ovalModalWasOpen = useRef(false);
  const skipSocketOvalPersistRef = useRef(false);
  const userTypeRef = useRef(user?.userType);
  userTypeRef.current = user?.userType;
  useEffect(() => {
    if (chromaticModalOpen && !chromaticModalWasOpen.current) {
      setChromaticPreviewDyops(dyops);
    }
    chromaticModalWasOpen.current = chromaticModalOpen;
  }, [chromaticModalOpen, dyops]);

  const handleChromaticPreviewDyops = useCallback((next: dyopsType) => {
    setChromaticPreviewDyops(next);
  }, []);

  useEffect(() => {
    if (ovalModalOpen && !ovalModalWasOpen.current) {
      setOvalPreviewDyops(dyops);
    }
    ovalModalWasOpen.current = ovalModalOpen;
  }, [ovalModalOpen, dyops]);

  const handleOvalPreviewDyops = useCallback((next: dyopsType) => {
    setOvalPreviewDyops(next);
  }, []);

  const scheduleDoctorOvalPersist = useCallback(
    (ovalMinorRatio: number, ovalAxisDeg: number) => {
      if (userTypeRef.current !== 'doctor') {
        return;
      }
      schedulePersistDoctorOvalPreference(dispatch, { ovalMinorRatio, ovalAxisDeg });
    },
    [dispatch],
  );

  const schedulePersistOvalFromSocket = useCallback(
    (next: dyopsType) => {
      if (userTypeRef.current !== 'doctor' || next.examMode !== 'oval') {
        return;
      }
      scheduleDoctorOvalPersist(next.ovalMinorRatio, next.ovalAxisDeg);
    },
    [scheduleDoctorOvalPersist],
  );

  const schedulePersistOvalFromSocketRef = useRef(schedulePersistOvalFromSocket);
  schedulePersistOvalFromSocketRef.current = schedulePersistOvalFromSocket;

  useEffect(() => {
    const detachWakeLock = attachScreenWakeLock();
    return () => {
      detachWakeLock();
    };
  }, []);

  useEffect(() => {
    socket.current = webSocketConnect(socketUrl, roomId, accessToken ? accessToken : '');

    socket.current.on('connect', () => {
      setIsConnected(true);
      dispatch(
        dyopsAction.getDyopsAction(socket.current.io.opts.query.roomId, undefined, (seedDyops) => {
          void apiApp.getRoomData(roomId).then((res) => {
            const patientTestType = res.data?.patientData?.testType as string | undefined;
            if (patientTestType) {
              setPatientSessionTestType(patientTestType);
            }
            const sessionType = resolveDoctorSessionTestType(testType, patientTestType);
            if (sessionType) {
              const next = applyInfantModeToDyops(seedDyops, sessionType);
              socket.current.emit('emitUpdateDoctor', { dyops: next });
              dispatch(dyopsAction.getDyops(next));
            } else {
              dispatch(dyopsAction.getDyops(seedDyops));
            }
          });
        }),
      );
    });

    socket.current.on('onUpdatePatient', (data: { testType?: string }) => {
      if (!data?.testType) {
        return;
      }

      setPatientSessionTestType(data.testType);

      if (!isInfantTestType(data.testType)) {
        if (dyopsRef.current.infantMode) {
          const next = applyInfantModeToDyops(dyopsRef.current, data.testType);
          socket.current.emit('emitUpdateDoctor', { dyops: next });
          dispatch(dyopsAction.getDyops(next));
        }
        return;
      }

      const next = applyInfantModeToDyops(dyopsRef.current, 'infant');
      dispatch(dyopsAction.getDyops(next));
      if (socket.current) {
        socket.current.emit('emitUpdateDoctor', { dyops: next });
      }
    });

    socket.current.on('onUpdateDoctor', (data: { dyops: dyopsType }) => {
      const sessionType = resolvedTestTypeRef.current;
      const shouldUseInfant =
        isInfantTestType(sessionType) || data.dyops.infantMode || isInfantTestType(data.dyops.result?.testType);
      const next = shouldUseInfant ? applyInfantModeToDyops(data.dyops, 'infant') : data.dyops;
      dispatch(dyopsAction.getDyops(next));
      if (skipSocketOvalPersistRef.current) {
        skipSocketOvalPersistRef.current = false;
        return;
      }
      schedulePersistOvalFromSocketRef.current(data.dyops);
    });

    socket.current.on('roomStatus', ({ room }: roomDataType) => {
      const hasPatient = Boolean(room.users.find((item) => item.type === 'patient'));
      setIsUserConnected(hasPatient);
      if (hasPatient) {
        void apiApp.getRoomData(roomId).then((res) => {
          const patientTestType = res.data?.patientData?.testType as string | undefined;
          if (!patientTestType) {
            return;
          }

          setPatientSessionTestType(patientTestType);

          if (!isInfantTestType(patientTestType)) {
            return;
          }

          const next = applyInfantModeToDyops(dyopsRef.current, 'infant');
          dispatch(dyopsAction.getDyops(next));
          if (socket.current) {
            socket.current.emit('emitUpdateDoctor', { dyops: next });
          }
        });
      }
    });

    socket.current.on('disconnect', () => {
      dispatch(dyopsAction.clearDyops(getChromaticFieldsFromDoctorUser(user), getOvalFieldsFromDoctorUser(user)));
      setIsConnected(false);
    });

    return () => {
      flushScheduledPersistDoctorOvalPreference();
      socket.current.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!chromaticModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setChromaticModalOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chromaticModalOpen]);

  const activeDyopRandom = (): 'left' | 'right' => {
    const random = Math.floor(Math.random() * (3 - 1) + 1);

    if (
      randomNumbers.length >= 3 &&
      randomNumbers.slice(randomNumbers.length - 3, randomNumbers.length).every((item) => item === random)
    ) {
      setRandomNumbers([...randomNumbers, random === 2 ? random - 1 : random + 1]);

      return (random === 2 ? random - 1 : random + 1) !== 2 ? 'right' : 'left';
    } else {
      setRandomNumbers([...randomNumbers, random]);

      return random % 2 !== 0 ? 'right' : 'left';
    }
  };

  const commitDyopsSocket = useCallback(
    (next: dyopsType) => {
      dispatch(dyopsAction.getDyops(next));
      if (isConnected && socket.current) {
        socket.current.emit('emitUpdateDoctor', { dyops: next });
      }
    },
    [dispatch, isConnected],
  );

  const screenRatio = appData.ratio || dyops.result.ratio;

  const modalDyopPreview = useMemo(() => {
    const ringDiameter = windowWidth <= 1100 ? 53 : 66;
    const patientDiameter = patientTestDyopDiameterPx(
      dyops,
      screenRatio || 1,
      String(dyops.result.distance || appData.distance),
      String(dyops.result.unit || appData.unit),
    );
    return {
      ringDiameter,
      spinDurationSec: dyopPreviewSpinDurationSec(ringDiameter, patientDiameter),
      rotation: dyops.rotation ? '' : 'counter-clockwise',
    };
  }, [windowWidth, dyops, screenRatio, appData.distance, appData.unit]);

  const commitDyopsChromatic = useCallback(
    (next: dyopsType) => {
      setChromaticPreviewDyops(next);
      commitDyopsSocket(next);
      if (user?.userType === 'doctor') {
        void apiDoctor
          .saveChromaticPreference({
            chromaticPresetId: next.chromaticPresetId,
            chromaticPrimary: next.chromaticPrimary,
            chromaticSecondary: next.chromaticSecondary,
          })
          .then((response) => {
            dispatch(
              userAction.setUserChromatic(
                response.data.chromaticPresetId,
                response.data.chromaticPrimary,
                response.data.chromaticSecondary,
              ),
            );
          })
          .catch((err) => {
            console.error(err);
          });
      }
    },
    [commitDyopsSocket, user?.userType],
  );

  const commitDyopsOval = useCallback(
    (next: dyopsType) => {
      setOvalPreviewDyops(next);
      skipSocketOvalPersistRef.current = true;
      commitDyopsSocket(next);
      scheduleDoctorOvalPersist(next.ovalMinorRatio, next.ovalAxisDeg);
    },
    [commitDyopsSocket, scheduleDoctorOvalPersist],
  );

  const handleExamModeChange = useCallback(
    (mode: ExamMode) => {
      if (mode === 'va') {
        flushScheduledPersistDoctorOvalPreference();
      }
      const withDistance = {
        ...dyops,
        result: {
          ...dyops.result,
          distance: dyops.result.distance || appData.distance,
          unit: dyops.result.unit || appData.unit,
          ratio: screenRatio,
        },
      };
      const next = applyExamModeToDyops(withDistance, mode, user, screenRatio);
      commitDyopsSocket(next);
    },
    [appData.distance, appData.unit, commitDyopsSocket, dyops, screenRatio, user],
  );

  const closeExamToolsModal = useCallback(() => {
    setExamToolsModalOpen(false);
  }, []);

  const openChromaticModalFromTools = useCallback(() => {
    setExamToolsModalOpen(false);
    setChromaticModalOpen(true);
  }, []);

  const openOvalModalFromTools = useCallback(() => {
    setExamToolsModalOpen(false);
    setOvalModalOpen(true);
  }, []);

  const renderExamToolButtons = (fromMenu: boolean) => (
    <div
      className={`doctor-test__chromatic-trigger-stack${fromMenu ? ' doctor-test__chromatic-trigger-stack--menu' : ''}`}
    >
      <button
        type="button"
        className="doctor-test__chromatic-modal-btn btn btn_submit"
        onClick={fromMenu ? openChromaticModalFromTools : () => setChromaticModalOpen(true)}
      >
        <span className="doctor-test__chromatic-modal-btn-inner">
          <ChromaticSchemeGlyph />
          <span className="doctor-test__chromatic-modal-btn-label">{strings.chromatic.openColorsModal}</span>
        </span>
      </button>
      {!isInfantSession ? <ExamModeSegment value={dyops.examMode ?? 'va'} onChange={handleExamModeChange} /> : null}
      {!isInfantSession && dyops.examMode === 'oval' ? (
        <button
          type="button"
          className="doctor-test__chromatic-modal-btn btn btn_submit"
          onClick={fromMenu ? openOvalModalFromTools : () => setOvalModalOpen(true)}
        >
          <span className="doctor-test__chromatic-modal-btn-inner">
            <OvalAdjustGlyph className="doctor-test__chromatic-modal-icon" />
            <span className="doctor-test__chromatic-modal-btn-label">{strings.oval.openPanel}</span>
          </span>
        </button>
      ) : null}
    </div>
  );

  useEffect(() => {
    if (!isConnected || !isInfantTestType(resolvedTestType) || dyopsRef.current.infantMode) {
      return;
    }
    const next = applyInfantModeToDyops(dyopsRef.current, 'infant');
    commitDyopsSocket(next);
  }, [isConnected, resolvedTestType, dyops.infantMode, commitDyopsSocket]);

  useInfantTargetJump(effectiveDyops, isConnected, socket.current, dispatch, resolvedTestType);

  useEffect(() => {
    if (!isInfantSession || !dyops.isStarted) {
      return;
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'l' && !effectiveDyops.infantLossRecorded) {
        handleDyopsControl(
          dispatch,
          socket.current,
          effectiveDyops,
          'lossOfFixation',
          history,
          isIntegration,
          activeDyopRandom,
          doctorName,
        );
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    effectiveDyops,
    effectiveDyops.infantLossRecorded,
    dyops.isStarted,
    dispatch,
    history,
    isIntegration,
    doctorName,
    activeDyopRandom,
  ]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.MouseEvent<HTMLButtonElement>) => {
    const actionId = e.currentTarget.id;
    if (dyops.examMode === 'oval' && (actionId === 'increase' || actionId === 'decrease' || actionId === 'notSure')) {
      return;
    }
    handleDyopsControl(
      dispatch,
      socket.current,
      effectiveDyops,
      e.currentTarget.id,
      history,
      isIntegration,
      activeDyopRandom,
      doctorName,
    );
  };

  // Mount exam modals on document.body so they stay in screen axes (portrait CSS-rotate
  // on the exam root would otherwise break overflow scroll and remap touch directions).
  /** Shared class for Colors / Oval / Options dialogs portaled to body. */
  const examModalClassName = `doctor-test__chromatic-modal doctor-test__chromatic-modal--with-preview${
    height ? ' doctor-test__chromatic-modal--screen-native' : ''
  }`;
  const examToolsModalClassName = `doctor-test__exam-tools-modal${
    height ? ' doctor-test__exam-tools-modal--screen-native' : ''
  }`;

  const ovalModalNode = ovalModalOpen ? (
    <div className="doctor-test__exam-modal-layer">
      <div
        className="doctor-test__chromatic-modal-backdrop"
        role="presentation"
        onClick={() => setOvalModalOpen(false)}
      />
      <div className={examModalClassName} role="dialog" aria-modal="true" aria-labelledby="oval-modal-title">
        <div className="doctor-test__chromatic-modal-head">
          <span id="oval-modal-title" className="doctor-test__chromatic-modal-title">
            {strings.oval.heading}
          </span>
          <button
            type="button"
            className="doctor-test__chromatic-modal-dismiss"
            aria-label={strings.oval.closePanel}
            onClick={() => setOvalModalOpen(false)}
          >
            <svg
              className="doctor-test__chromatic-modal-dismiss-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden={true}
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="doctor-test__chromatic-modal-main">
          <div className="doctor-test__chromatic-modal-split">
            <div className="doctor-test__chromatic-modal-controls">
              <OvalRefractionPanel
                dyops={dyops}
                screenRatio={screenRatio}
                disabled={false}
                onPreviewDyopsChange={handleOvalPreviewDyops}
                onCommit={commitDyopsOval}
              />
            </div>
            <div className="doctor-test__chromatic-modal-preview">
              <DoctorTestModalDyopPreview
                dyops={ovalPreviewDyops ?? dyops}
                ringDiameter={modalDyopPreview.ringDiameter}
                spinDurationSec={modalDyopPreview.spinDurationSec}
                rotation={modalDyopPreview.rotation}
              />
            </div>
          </div>
          <div className="doctor-test__chromatic-modal-foot">
            <div className="doctor-test__chromatic-modal-foot-inner doctor-test__chromatic-modal-foot-inner--start">
              <p className="doctor-test__chromatic-modal-foot-slot">{strings.oval.readoutFooterLine1}</p>
              <p className="doctor-test__chromatic-modal-foot-slot">{strings.oval.readoutFooterLine2}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const chromaticModalNode = chromaticModalOpen ? (
    <div className="doctor-test__exam-modal-layer">
      <div
        className="doctor-test__chromatic-modal-backdrop"
        role="presentation"
        onClick={() => setChromaticModalOpen(false)}
      />
      <div className={examModalClassName} role="dialog" aria-modal="true" aria-labelledby="chromatic-modal-title">
        <div className="doctor-test__chromatic-modal-head">
          <span id="chromatic-modal-title" className="doctor-test__chromatic-modal-title">
            {strings.chromatic.heading}
          </span>
          <button
            type="button"
            className="doctor-test__chromatic-modal-dismiss"
            aria-label={strings.chromatic.closePanel}
            onClick={() => setChromaticModalOpen(false)}
          >
            <svg
              className="doctor-test__chromatic-modal-dismiss-icon"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden={true}
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="doctor-test__chromatic-modal-main">
          <div className="doctor-test__chromatic-modal-split">
            <div className="doctor-test__chromatic-modal-controls">
              <ChromaticPresetPanel
                dyops={dyops}
                disabled={false}
                onCommit={commitDyopsChromatic}
                compact
                variant="embedded"
                onChromaticWarningsChange={handleChromaticWarningsChange}
                onPreviewDyopsChange={handleChromaticPreviewDyops}
              />
            </div>
            <div className="doctor-test__chromatic-modal-preview">
              <DoctorTestModalDyopPreview
                dyops={chromaticPreviewDyops ?? dyops}
                forceCircular
                ringDiameter={modalDyopPreview.ringDiameter}
                spinDurationSec={modalDyopPreview.spinDurationSec}
                rotation={modalDyopPreview.rotation}
              />
            </div>
          </div>
          <div className="doctor-test__chromatic-modal-foot">
            <ChromaticPresetWarnings
              contrastLow={chromaticModalWarnings.contrastLow}
              customInvalid={chromaticModalWarnings.customInvalid}
            />
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const examToolsModalNode =
    isMobile && examToolsModalOpen ? (
      <div className="doctor-test__exam-modal-layer">
        <div className="doctor-test__chromatic-modal-backdrop" role="presentation" onClick={closeExamToolsModal} />
        <div
          className={examToolsModalClassName}
          role="dialog"
          aria-modal="true"
          aria-labelledby="exam-tools-modal-title"
        >
          <div className="doctor-test__chromatic-modal-head">
            <span id="exam-tools-modal-title" className="doctor-test__chromatic-modal-title">
              {strings.testPage.examToolsMenuTitle}
            </span>
            <button
              type="button"
              className="doctor-test__chromatic-modal-dismiss"
              aria-label={strings.chromatic.closePanel}
              onClick={closeExamToolsModal}
            >
              <svg
                className="doctor-test__chromatic-modal-dismiss-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden={true}
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="doctor-test__exam-tools-modal-body">{renderExamToolButtons(true)}</div>
        </div>
      </div>
    ) : null;

  return (
    <div
      className={`doctor-test${height ? ' rotate' : ''}${isMobile ? ' doctor-test--mobile' : ''}${
        landscapeMobile ? ' doctor-test--landscape-mobile' : ''
      }${controlsVisible ? ' doctor-test--running' : ''}${examOverlayOpen ? ' doctor-test--overlay-open' : ''}`}
      style={height ? { width: `${window.innerHeight}px` } : {}}
    >
      {!appLoading.status && isConnected && (
        <>
          {ovalModalNode ? createPortal(ovalModalNode, document.body) : null}
          {chromaticModalNode ? createPortal(chromaticModalNode, document.body) : null}
          {examToolsModalNode ? createPortal(examToolsModalNode, document.body) : null}
          <div className="doctor-test__top-bar">
            <div className="doctor-test__session-type" aria-live="polite">
              {sessionTestTypeLabel ?? strings.testPage.sessionTestTypeUnknown}
            </div>
            <div className="doctor-test__chromatic-trigger">
              {isMobile ? (
                <button
                  type="button"
                  className="doctor-test__exam-tools-trigger btn btn_submit"
                  onClick={() => setExamToolsModalOpen(true)}
                  aria-label={strings.testPage.openExamToolsMenu}
                  aria-haspopup="dialog"
                  aria-expanded={examToolsModalOpen}
                >
                  {strings.testPage.openExamToolsMenu}
                </button>
              ) : (
                renderExamToolButtons(false)
              )}
            </div>
          </div>
          <div className="doctor-test__dyops">
            <Dyops
              diametr={dyopStageDiameter}
              eyeSeparationPx={dyopEyeSeparationPx}
              label={true}
              dyops={effectiveDyops}
              isUserConnected={isUserConnected}
              forceInfantLayout={isInfantSession}
            />
          </div>
          <div className="doctor-test__start-slot">
            {showStartButton ? (
              <button
                disabled={startDisabled}
                onClick={handleClick}
                id="start"
                className="dyops__start-btn btn btn_submit"
              >
                {isInfantSession || (!dyops.closeRightEye && !dyops.closeLeftEye)
                  ? strings.connectPage.labels.startTest
                  : dyops.closeLeftEye
                  ? strings.selfTest.testRight
                  : strings.selfTest.testLeft}
              </button>
            ) : null}
          </div>
          <div className="doctor-test__lower">
            <div className={`dyops__control-btns${controlsVisible ? ' visible' : ''}`}>
              <div className={`buttons${isInfantSession ? ' buttons--infant' : ''}`}>
                <div className="buttons__item" id="pause" onClick={handleClick}>
                  <div className="icon__wrapper">
                    <button className={`icon${!dyops.pause ? ' icon-pause' : ' icon-play'}`} />
                  </div>
                  <span>{!dyops.pause ? strings.testPage.controls.pause : strings.testPage.controls.start}</span>
                </div>
                {isInfantSession ? (
                  <>
                    <div className="buttons__item" id="increase" onClick={handleClick}>
                      <div className="icon__wrapper">
                        <button className="icon icon-larger" />
                      </div>
                      <span>{strings.infant.larger}</span>
                    </div>
                    <div className="buttons__item" id="decrease" onClick={handleClick}>
                      <div className="icon__wrapper">
                        <button className="icon icon-eye" />
                      </div>
                      <span>{strings.infant.stillTracking}</span>
                    </div>
                    <div
                      className={`buttons__item${dyops.infantLossRecorded ? ' buttons__item--disabled' : ''}`}
                      id="lossOfFixation"
                      title={strings.infant.lossOfFixationHint}
                      onClick={dyops.infantLossRecorded ? undefined : handleClick}
                    >
                      <div className="icon__wrapper">
                        <button
                          type="button"
                          className="icon icon-eye-lost"
                          title={strings.infant.lossOfFixationHint}
                          disabled={dyops.infantLossRecorded}
                        />
                      </div>
                      <span>{strings.infant.lossOfFixation}</span>
                    </div>
                    <div className="buttons__item" id="stopTest" onClick={handleClick}>
                      <div className="icon__wrapper">
                        <button type="button" className="icon icon-zoom-out" title={strings.infant.finishTest} />
                      </div>
                      <span>{strings.infant.finishTest}</span>
                    </div>
                  </>
                ) : dyops.examMode === 'va' ? (
                  <>
                    <div className="buttons__item" id="increase" onClick={handleClick}>
                      <div className="icon__wrapper">
                        <button className="icon icon-zoom-in" />
                      </div>
                      <span>{strings.testPage.controls.incorrect}</span>
                    </div>
                    <div className="buttons__item" id="decrease" onClick={handleClick}>
                      <div className="icon__wrapper">
                        <button className="icon icon-zoom-out" />
                      </div>
                      <span>{strings.testPage.controls.correct}</span>
                    </div>
                    <div className="buttons__item" id="notSure" onClick={handleClick}>
                      <div className="icon__wrapper">
                        <button className="icon icon-help" />
                      </div>
                      <span>{strings.testPage.controls.notSure}</span>
                    </div>
                  </>
                ) : (
                  <div className="buttons__item" id="finishOvalEye" onClick={handleClick}>
                    <div className="icon__wrapper">
                      <button className="icon icon-zoom-out" />
                    </div>
                    <span>{strings.oval.finishEye}</span>
                  </div>
                )}
                {!isInfantSession ? (
                  <>
                    <div className="buttons__item" id="rotation" onClick={handleClick}>
                      <div className="icon__wrapper">
                        <button className="icon icon-rotation" />
                      </div>
                      <span>{strings.testPage.controls.direction}</span>
                    </div>
                    <div className="buttons__item" id="active" onClick={handleClick}>
                      <div className="icon__wrapper">
                        <button className={`icon${dyops.active === 'left' ? ' icon-prev' : ' icon-next'}`} />
                      </div>
                      <span>
                        {dyops.active === 'right'
                          ? strings.testPage.controls.toLeft
                          : strings.testPage.controls.toRight}
                      </span>
                    </div>
                  </>
                ) : null}
                <div className="buttons__item" id="restart" onClick={handleClick}>
                  <div className="icon__wrapper">
                    <button className="icon icon-restart" />
                  </div>
                  <span>{strings.testPage.controls.restart}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DoctorTest;
